// React Imports
import { useEffect, useState, Fragment } from "react";

// External Libraries
import { Button } from "primereact/button";
import { Tooltip } from "primereact/tooltip";
import { FullScreenLoader } from "sj-ui-toolkit/src/components";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// Hooks & Helpers
import { fetchAvailableTenants } from "sj-ui-toolkit/src/api";
import { signOut } from "sj-ui-toolkit/src/utils";
import { useLoginFlow } from "sj-ui-toolkit/src/hooks/useLoginFlow";

// Main component
export default function SelectTenant() {
  const { doTenantSelectionAndRedirect } = useLoginFlow();
  const [licenseError, setLicenseError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Awaited<ReturnType<typeof fetchAvailableTenants>>>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setIsLoading(true);

    try {
      const tenants = await fetchAvailableTenants();
      if (tenants) {
        setTenants(tenants);
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTenant = async (tenantId: string) => {
    const success = await doTenantSelectionAndRedirect(tenantId);
    if (!success) {
      setLicenseError(
        "You are not currently licensed for this tenant. Please select another tenant."
      );
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen">
        <FullScreenLoader showText={false} />
      </div>
    );
  }

  return (
    <LoginLayout
      title={isLoading ? "Loading Tenants..." : "Select a Tenant to Continue"}
      noFooter={true}
    >
      <div className="flex flex-col w-full gap-2">
        {licenseError && <div className="text-center text-red-600 mb-4">{licenseError}</div>}
        {isLoading ? (
          <div className="text-center">
            <span className="sr-only">Loading tenants...</span>
            <i className="pi pi-spinner pi-spin" style={{ fontSize: "2rem" }}></i>
          </div>
        ) : tenants.length === 0 ? (
          <div className="text-center">
            <p className="mb-4">No tenants available</p>
            <Button
              className="w-full mt-3"
              data-testid="select-tenant-back-to-login-button"
              severity="secondary"
              onClick={() => signOut()}
              label="Back to Login"
            />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <Tooltip target=".disabled-button" className="max-w-96" position="bottom" />
              {tenants.map(tenant =>
                tenant.userLicensed ? (
                  <Button
                    key={tenant.id}
                    className="justify-start w-full text-left"
                    data-testid={`select-tenant-button-${tenant.id}`}
                    onClick={() => handleSelectTenant(tenant.name)}
                    label={tenant.displayName}
                    icon="pi pi-building"
                  />
                ) : (
                  <Fragment key={tenant.id}>
                    <span
                      className="disabled-button"
                      data-pr-tooltip="You do not have a license for this tenant. Please get in touch with your administrator to access this tenant."
                    >
                      <Button
                        className="justify-start w-full text-left"
                        data-testid={`select-tenant-button-${tenant.id}-disabled`}
                        label={tenant.displayName}
                        icon="pi pi-building"
                        disabled={true}
                      />
                    </span>
                  </Fragment>
                )
              )}
            </div>
            <Button
              className="w-full mt-3 text-left"
              data-testid="select-tenant-logout-button"
              outlined
              severity="secondary"
              onClick={() => signOut()}
              label="Log out"
              icon="pi pi-sign-out"
            />
          </>
        )}
      </div>
    </LoginLayout>
  );
}
